import {Injectable} from '@angular/core';
import * as echarts from 'echarts';
import {TaskModel} from '../../../models/task.model';
import {ICRMTasksCalendarWidgetModel} from '../model/widget.model';

interface IChartDayData {
    dayUnit: string,
    tasks: TaskModel[]
}

export interface IPeriodChart {
    month: number,
    year: number
}

@Injectable({
    providedIn: 'root'
})
export class WidgetTaskCalendarService {
    private chartData: {
        legend: string[],
        taskPieChartData: echarts.PieSeriesOption[]
        calendarSet: IChartDayData[],
        calendarSetArray: [string, number, TaskModel[]][],
        taskFlatData: TaskModel[]
    }
    private cellSize = [70, 70];
    private colorTasks = ['#4b7896ff', '#2acaa2ff', '#e26470', '#8c56c9', ' #194abd']
    private colorMap: Map<string, string> = new Map()
    public addInfoForTip: string

    prepareDataForChart(widgetCalendar: ICRMTasksCalendarWidgetModel, periodChart: IPeriodChart) {
        this.chartData = {
            legend: [],
            taskPieChartData: [],
            calendarSet: [],
            calendarSetArray: [],
            taskFlatData: []
        }
        this.chartData.taskFlatData = Object.values(widgetCalendar.content).flat()
        this.chartData.legend = Array.from(new Set(this.chartData.taskFlatData.map(el => el.task_board_name)))
        this.chartData.legend.forEach((el, index) => this.colorMap.set(el, this.colorTasks[index]))
        const startDate = +echarts.time.parse(`${periodChart.year}-${periodChart.month}-01`);
        const endDate = +echarts.time.parse(`${periodChart.year}-${periodChart.month}-32`);
        const dayTime = 3600 * 24 * 1000;
        for (let dayUnit = startDate; dayUnit < endDate; dayUnit += dayTime) {
            let tasks = widgetCalendar.content[echarts.time.format(dayUnit, '{yyyy}-{MM}-{dd}', false)];
            tasks = tasks ? tasks.sort((a, b) => a.task_board_name > b.task_board_name ? -1 : 1) : tasks;
            this.chartData.calendarSet.push({
                dayUnit: echarts.time.format(dayUnit, '{yyyy}-{MM}-{dd}', false),
                tasks: widgetCalendar.content[echarts.time.format(dayUnit, '{yyyy}-{MM}-{dd}', false)]
            });
        }
        this.chartData.calendarSet.forEach(el => this.chartData.calendarSetArray.push([el.dayUnit, el.tasks?.length, el.tasks]))
        let pieChartData: echarts.PieSeriesOption[] = []
        this.chartData.calendarSet.forEach((unit, index) => {
            let chartSet: { name: string, value: number, tasks: TaskModel[] }[] = []
            if (unit.tasks?.length > 0) {
                this.chartData.legend.forEach(taskCode => {
                    let tasksWithCode = unit.tasks?.filter(task => task.task_board_name === taskCode)
                    tasksWithCode ?
                        chartSet.push({
                            name: taskCode,
                            value: tasksWithCode.length,
                            tasks: tasksWithCode
                        }) : null;
                })
                pieChartData.push({
                    type: 'pie',
                    id: 'pie-' + index,
                    center: unit.dayUnit,
                    radius: [20, 30],
                    padAngle: 3,
                    coordinateSystem: 'calendar',
                    label: {
                        formatter: '{c}',
                        show: false,
                        fontWeight: 'bold',
                        fontSize: 16
                    },
                    tooltip: {
                        position: [10, 10],
                        padding: [5, 30, 5, 10],
                        formatter: (params) => this.getTooltip(params, periodChart.month),
                        textStyle: {
                            fontSize: 14
                        }
                    },
                    data: chartSet
                } as echarts.PieSeriesOption)
            }
        })
        this.chartData.taskPieChartData = pieChartData
    }

    getTooltip(params, month: number): string {
        let taskData = this.filerTaskDataByParams(params, month)
        if (taskData === undefined) {
            return ''
        }
        let template = `
            <ul class="list-group" >
                <li class="list-group-item date-tooltip-item date-tooltip-item-header" >
                <span class="date-tooltip-item-alias"> Код </span>
                <span class="date-tooltip-item-title"> Название </span>
                <span class="date-tooltip-item-status"> Статус </span>
                <span class="date-tooltip-item-updated"> Обновление</span>
            
            </li>`
        taskData.forEach(task => {
            let newItem = ` 
                <li class="list-group-item date-tooltip-item" >
                    <span style="display:inline-block;margin-right:4px; margin-top:5px;border-radius:10px;width:10px;height:10px;background-color:${this.colorMap.get(task.task_board_name)};">
                    </span>
                    <span class="date-tooltip-item-alias"> ${task.task_alias}  </span>
                    <span class="date-tooltip-item-title"> ${task.title} </span>
                    <span class="date-tooltip-item-status"> ${task.status_current.label} </span>
                    <span class="date-tooltip-item-updated"> ${echarts.time.format(task.updated_at, '{dd}-{MM}-{yy} ({HH}:{mm})', false)} </span>
                </li>`
            template = template + newItem
        })
        template = template + `<small>${this.addInfoForTip}</small><ul>`
        return template
    }

    filerTaskDataByParams(params: any, month: number): TaskModel[] {
        let taskData: TaskModel[] = []
        params.componentType === 'legend' ?
            taskData = structuredClone(this.chartData.taskFlatData)
                .filter(el => el.task_board_name === params.name && month === +el.due_time.slice(5, 7)) : null;
        params.componentSubType === 'pie' ? taskData = params.data.tasks : null;
        params.componentSubType === 'scatter' ? taskData = taskData = params.value[2] : null;
        return taskData
    }

    setChartOptions(periodChart: IPeriodChart, weekDayAbbr: string[]): any {
        let option = {
            color: this.colorTasks,
            tooltip: {},
            legend: {
                data: this.chartData.legend,
                position: 'center',
                tooltip: {
                    show: true,
                    position: [10, 30],
                    formatter: (params) => this.getTooltip(params, periodChart.month)
                }
            },
            calendar: {
                top: 'middle',
                left: 'center',
                orient: 'vertical',
                cellSize: this.cellSize,
                splitLine: {
                    lineStyle: {
                        color: 'transparent' // This will change the border color
                    }
                },
                itemStyle: {
                    borderColor: '#f0eeeeff',
                },
                yearLabel: {
                    show: false,
                    fontSize: 30,
                },
                dayLabel: {
                    margin: 10,
                    firstDay: 1,
                    fontSize: 14,
                    color: 'grey',
                    nameMap: weekDayAbbr
                },
                monthLabel: {
                    show: false,
                    fontSize: 16,
                },
                range: [`${periodChart.year}-${periodChart.month}`]
            },
            series: [
                {
                    id: 'label',
                    type: 'scatter',
                    coordinateSystem: 'calendar',
                    symbolSize: 0,
                    tooltip: {
                        position: [10, 10],
                        padding: [5, 30, 5, 10],
                        formatter: (params) => this.getTooltip(params, periodChart.month),
                        textStyle: {
                            fontSize: 14
                        }
                    },
                    label: {
                        show: true,
                        formatter: (params: any) => {
                            return echarts.time.format(params.value[0], '{dd}', false);
                        },
                        fontSize: 14,
                        fontWeight: 'bold',
                        color: 'grey',
                    },
                    data: this.chartData.calendarSetArray
                },
                ...this.chartData.taskPieChartData
            ]
        };
        return option;
    }
}